

















import { Component, Mixins, Prop, Emit } from "vue-property-decorator";
import { Color, LineStyle, LineKinds, Point } from "../../domain/drawing";
import { BrowserUtility, BrowserKinds } from "../../utilities";
import { default as EraserIndicator } from "./EraserIndicator.vue";

@Component({
  components: {
    EraserIndicator,
  }
})
export default class DrawableCanvas extends Mixins() {
  static readonly firstDrawOffset = BrowserUtility.getBrowserKind() === BrowserKinds.InternetExplorer ? 0.1 : 0;
  private _image: HTMLImageElement;
  private _isDrawing = false;
  private _lastX: number = null;
  private _lastY: number = null;
  private _undoStack: ImageData[] = [];
  private _redoStack: ImageData[] = [];

  $refs!: {
    canvas: HTMLCanvasElement,
  };

  @Prop({ default: 5 })
  stackMaxSize: number;

  @Prop()
  scale: number;

  @Prop()
  imageSource: string;

  @Prop()
  width: number;

  @Prop()
  height: number;

  @Prop({ default: () => new LineStyle(new Color("#000000"), LineKinds.Normal, 1) })
  lineStyle: LineStyle;

  @Prop({ default: false })
  isEraser: boolean;

  data() {
    return {
      mouseLocation: new Point(-1, -1),
    };
  }
  mouseLocation: Point;

  get isErasing(): boolean {
    return this.isEraser
        && this._isDrawing;
  }

  get isCanvasContained(): boolean {
    return (0 <= this.mouseLocation.x)
        && (this.mouseLocation.x <= this.width)
        && (0 <= this.mouseLocation.y)
        && (this.mouseLocation.y <= this.height);
  }

  get canvas(): HTMLCanvasElement {
    return this.$refs.canvas;
  }

  get context(): CanvasRenderingContext2D {
    return this.canvas.getContext("2d");
  }

  mounted(): void {
    this._undoStack = [];
    this._redoStack = [];
    this.stackUpdated(0, 0);
    this.load();
  }

  private load(): void {
    this._image = new Image();
    if (this.imageSource) {
      this._image.onload = () => this.onImageLoaded();
      this._image.src = this.imageSource;
    } else {
      this.context.clearRect(0, 0, this.width, this.height);
      this.loaded();
    }
  }

  private onImageLoaded(): void {
    this.context.drawImage(this._image, 0, 0, this.width, this.height);
    this.loaded();
  }

  reload(): void {
    this.load();
  }

  trackMouse(event: PointerEvent): void {
    this.mouseLocation = Point.from(this.toCanvasPositionFromClient(event.clientX, event.clientY));
  }

  trackMouseOut(event: PointerEvent): void {
    this.mouseLocation = new Point(-1, -1);
  }

  onMouseDown(event: PointerEvent): void {
    if (event.buttons !== 1) {
      return;
    }
    this.trackMouse(event);
    this.dragStart();
    this.draw(this.mouseLocation.x, this.mouseLocation.y);
  }

  onMouseMove(event: PointerEvent): void {
    if (event.buttons !== 1) {
      this.dragEnd();
      return;
    }
    const pos = this.toCanvasPositionFromClient(event.clientX, event.clientY);
    this.draw(pos.x, pos.y);
  }

  onMouseUp(event: PointerEvent): void {
    this.onMouseMove(event);
  }

  trackMouseEnter(event: PointerEvent): void {
    this.trackMouse(event);
    if (event.buttons === 1) {
      this.dragStart();
    }
  }

  private toCanvasPositionFromClient(x: number, y: number): {x: number, y: number } {
    const d = this.canvas.ownerDocument;
    const w = d.defaultView;
    const r = this.canvas.getBoundingClientRect();
    const s = w.getComputedStyle(this.canvas, "");
    const px = parseFloat(s.paddingLeft);
    const py = parseFloat(s.paddingTop);
    const bx = parseFloat(s.borderLeftWidth);
    const by = parseFloat(s.borderTopWidth);
    const sx = this.canvas.width / parseFloat(s.width);
    const sy = this.canvas.height / parseFloat(s.height);
    return {
      x: (x - bx - px - r.left) * (sx) / this.scale,
      y: (y - by - py - r.top) * (sy) / this.scale
    };
  }

  private dragStart(): void {
    document.addEventListener("pointermove", this.onMouseMove);
    this.beforeDraw();
    this.context.beginPath();
    this._isDrawing = true;
  }

  private dragEnd(): void {
    document.removeEventListener("pointermove", this.onMouseMove);
    this.context.closePath();
    this._isDrawing = false;
    this._lastX = null;
    this._lastY = null;
  }

  private beforeDraw(): void  {
    this._redoStack = [];
    if (this._undoStack.length >= this.stackMaxSize) {
        this._undoStack.pop();
    }
    const context = this.context;
    this._undoStack.unshift(context.getImageData(0, 0, this.width, this.height));
    this.stackUpdated(this._undoStack.length, this._redoStack.length);
  }

  private draw(x: number, y: number): void {
    if (!this._isDrawing) { return; }
    const context = this.context;
    context.save();
    if (this.isEraser) {
      context.globalCompositeOperation = "destination-out";
    }
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = this.lineStyle.width;
    context.strokeStyle = this.lineStyle.color.value;
    if (this._lastX == null || this._lastY == null) {
      // IEではmoveTo()とlineTo()が同じ位置の場合描画が行われない。点対応のために少しずらすオフセットを用意する
      context.moveTo(x - DrawableCanvas.firstDrawOffset, y - DrawableCanvas.firstDrawOffset);
    } else {
      context.moveTo(this._lastX, this._lastY);
    }
    context.lineTo(x, y);
    context.stroke();
    context.restore();

    this._lastX = x;
    this._lastY = y;
  }

  undo(): void {
    if (this._undoStack.length <= 0) { return; }
    const context = this.context;
    this._redoStack.unshift(context.getImageData(0, 0, this.width, this.height));
    const imageData = this._undoStack.shift();
    context.putImageData(imageData, 0, 0);
    this.stackUpdated(this._undoStack.length, this._redoStack.length);
  }

  redo(): void {
    if (this._redoStack.length <= 0) { return; }
    const context = this.context;
    this._undoStack.unshift(context.getImageData(0, 0, this.width, this.height));
    const imageData = this._redoStack.shift();
    context.putImageData(imageData, 0, 0);
    this.stackUpdated(this._undoStack.length, this._redoStack.length);
  }

  toImageText(): string {
    let data = this.canvas.toDataURL("image/png");
    if (data.startsWith("data:image/png;base64,")) {
      data = data.substring(22);
    }
    return data;
  }

  @Emit()
  loaded(): void {}

  @Emit()
  stackUpdated(undo: number, redo: number): void {}
}
